import React, { useState } from 'react';
import { OfficerLayout } from '../../components/layout/OfficerLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { ChevronDown, ChevronUp, ChevronRight, CheckCircle2, Circle, ExternalLink, Search, Clock, Calendar, Camera, Upload, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
interface Task {
  id: string;
  title: string;
  dueDate: string;
  completed: boolean;
  materialLink?: string;
  description?: string;
  requirements?: string[];
}
interface Program {
  id: string;
  title: string;
  deadline: string;
  status: 'todo' | 'progress' | 'done';
  tasks: Task[];
}
const MOCK_PROGRAMS: Program[] = [{
  id: '1',
  title: 'Pilah Sampah dari Rumah',
  deadline: '28 Feb 2025',
  status: 'progress',
  tasks: [{
    id: 't1',
    title: 'Sosialisasi ke 5 RT Target',
    dueDate: '20 Feb',
    completed: true,
    description: 'Lakukan sosialisasi door-to-door atau kumpulkan warga di balai RT.',
    requirements: ['Foto kegiatan', 'Daftar hadir warga']
  }, {
    id: 't2',
    title: 'Distribusi Poster Jadwal Angkut',
    dueDate: '22 Feb',
    completed: true,
    description: 'Tempel poster jadwal di lokasi strategis.',
    requirements: ['Foto poster tertempel']
  }, {
    id: 't3',
    title: 'Monitoring Pemilahan Awal',
    dueDate: '25 Feb',
    completed: false,
    materialLink: '#',
    description: 'Cek sampel sampah rumah tangga apakah sudah terpilah.',
    requirements: ['Foto sampah terpilah', 'Data sampling']
  }, {
    id: 't4',
    title: 'Laporan Evaluasi Bulanan',
    dueDate: '28 Feb',
    completed: false,
    description: 'Submit laporan evaluasi via aplikasi.',
    requirements: ['Dokumen laporan']
  }]
}, {
  id: '2',
  title: 'HPSN 2025: Atasi Sampah Plastik',
  deadline: '21 Feb 2025',
  status: 'todo',
  tasks: [{
    id: 't5',
    title: 'Koordinasi Lokasi Aksi Bersih',
    dueDate: '15 Feb',
    completed: false
  }, {
    id: 't6',
    title: 'Registrasi Komunitas Peserta',
    dueDate: '18 Feb',
    completed: false
  }, {
    id: 't7',
    title: 'Pelaksanaan Event',
    dueDate: '21 Feb',
    completed: false
  }]
}, {
  id: '3',
  title: 'Ramadhan Minim Sampah',
  deadline: '10 Mar 2025',
  status: 'todo',
  tasks: [{
    id: 't8',
    title: 'Edukasi Takjil Tanpa Plastik',
    dueDate: '1 Mar',
    completed: false
  }, {
    id: 't9',
    title: 'Kampanye Habiskan Makanan',
    dueDate: '5 Mar',
    completed: false
  }]
}];
export function ChecklistPage() {
  const [expandedId, setExpandedId] = useState<string | null>('1');
  const [programs, setPrograms] = useState(MOCK_PROGRAMS);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTask, setSelectedTask] = useState<{
    programId: string;
    task: Task;
  } | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  const toggleTask = (programId: string, taskId: string) => {
    setPrograms(programs.map(p => {
      if (p.id !== programId) return p;
      return {
        ...p,
        tasks: p.tasks.map(t => {
          if (t.id !== taskId) return t;
          return {
            ...t,
            completed: !t.completed
          };
        })
      };
    }));
  };
  const filteredPrograms = programs.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase());
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'todo') return matchesSearch && p.status === 'todo';
    if (activeTab === 'progress') return matchesSearch && p.status === 'progress';
    if (activeTab === 'done') return matchesSearch && p.status === 'done';
    return matchesSearch;
  });
  return <OfficerLayout title="Checklist Program" data-id="element-2722">
      {/* Search & Filter */}
      <div className="mb-4 space-y-3" data-id="element-2723">
        <div className="relative" data-id="element-2724">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-2725" />
          <Input placeholder="Cari program atau tugas..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2726" />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-id="element-2727">
          {['all', 'todo', 'progress', 'done'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={cn('px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors', activeTab === tab ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50')} data-id="element-2728">
              {tab === 'all' ? 'Semua' : tab === 'todo' ? 'Belum Mulai' : tab === 'progress' ? 'Berjalan' : 'Selesai'}
            </button>)}
        </div>
      </div>

      <div className="space-y-4" data-id="element-2729">
        {filteredPrograms.map(program => {
        const completedTasks = program.tasks.filter(t => t.completed).length;
        const totalTasks = program.tasks.length;
        const progress = Math.round(completedTasks / totalTasks * 100);
        const isExpanded = expandedId === program.id;
        return <Card key={program.id} className="overflow-hidden border-l-4 border-l-primary-600 shadow-sm" data-id="element-2730">
              <div className="p-4 flex items-start justify-between cursor-pointer active:bg-gray-50 transition-colors" onClick={() => toggleExpand(program.id)} data-id="element-2731">
                <div className="flex-1" data-id="element-2732">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2733">
                    <Badge variant={program.status === 'done' ? 'success' : program.status === 'progress' ? 'warning' : 'secondary'} className="text-[10px] px-2 py-0.5" data-id="element-2734">
                      {program.status === 'done' ? 'Selesai' : program.status === 'progress' ? 'Berjalan' : 'Belum Mulai'}
                    </Badge>
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2735">
                      <Clock className="h-3 w-3" data-id="element-2736" /> {program.deadline}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-2737">
                    {program.title}
                  </h3>

                  {/* Progress Bar */}
                  <div className="flex items-center gap-2" data-id="element-2738">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2739">
                      <div className="h-full bg-primary-600 transition-all duration-500" style={{
                    width: `${progress}%`
                  }} data-id="element-2740" />
                    </div>
                    <span className="text-xs font-medium text-gray-600" data-id="element-2741">
                      {completedTasks}/{totalTasks}
                    </span>
                  </div>
                </div>
                <div className="ml-3 mt-1 text-gray-400" data-id="element-2742">
                  {isExpanded ? <ChevronUp className="h-5 w-5" data-id="element-2743" /> : <ChevronDown className="h-5 w-5" data-id="element-2744" />}
                </div>
              </div>

              {/* Tasks List */}
              <div className={cn('bg-gray-50 transition-all duration-300 overflow-hidden', isExpanded ? 'max-h-[500px] border-t' : 'max-h-0')} data-id="element-2745">
                <div className="p-4 space-y-3" data-id="element-2746">
                  {program.tasks.map(task => <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-white transition-colors cursor-pointer" onClick={e => {
                // Prevent opening modal if clicking checkbox directly
                if ((e.target as HTMLElement).closest('button')) return;
                setSelectedTask({
                  programId: program.id,
                  task
                });
                setIsTaskModalOpen(true);
              }} data-id="element-2747">
                      <button onClick={() => toggleTask(program.id, task.id)} className={cn('mt-0.5 flex-shrink-0 transition-colors p-1 -m-1', task.completed ? 'text-primary-600' : 'text-gray-300 hover:text-gray-400')} data-id="element-2748">
                        {task.completed ? <CheckCircle2 className="h-5 w-5" data-id="element-2749" /> : <Circle className="h-5 w-5" data-id="element-2750" />}
                      </button>
                      <div className="flex-1" data-id="element-2751">
                        <p className={cn('text-sm text-gray-800 leading-tight mb-1', task.completed && 'line-through text-gray-400')} data-id="element-2752">
                          {task.title}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-2753">
                          <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2754">
                            <Calendar className="h-3 w-3" data-id="element-2755" /> {task.dueDate}
                          </span>
                          {task.materialLink && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded" data-id="element-2756">
                              Ada Materi
                            </span>}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 self-center" data-id="element-2757" />
                    </div>)}
                </div>
              </div>
            </Card>;
      })}
      </div>

      {/* Task Detail Modal */}
      <Modal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} title={selectedTask?.task.title || 'Detail Tugas'} size="sm" className="mb-0 rounded-b-none sm:rounded-b-lg sm:mb-auto fixed bottom-0 sm:relative w-full sm:w-auto" footer={<div className="w-full flex gap-2" data-id="element-2759">
            <Button variant="outline" className="flex-1" onClick={() => setIsTaskModalOpen(false)} data-id="element-2760">
              Tutup
            </Button>
            <Button className={`flex-1 ${selectedTask?.task.completed ? 'bg-gray-500' : 'bg-primary-600'}`} onClick={() => {
        if (selectedTask) {
          toggleTask(selectedTask.programId, selectedTask.task.id);
          setIsTaskModalOpen(false);
        }
      }} data-id="element-2761">
              {selectedTask?.task.completed ? 'Batalkan Selesai' : 'Tandai Selesai'}
            </Button>
          </div>} data-id="element-2758">
        {selectedTask && <div className="space-y-4" data-id="element-2762">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border" data-id="element-2763">
              <div className="flex items-center gap-2" data-id="element-2764">
                <Calendar className="h-4 w-4 text-gray-500" data-id="element-2765" />
                <span className="text-sm text-gray-700" data-id="element-2766">
                  Deadline: {selectedTask.task.dueDate}
                </span>
              </div>
              <Badge variant={selectedTask.task.completed ? 'success' : 'secondary'} data-id="element-2767">
                {selectedTask.task.completed ? 'Selesai' : 'Belum Selesai'}
              </Badge>
            </div>

            <div data-id="element-2768">
              <h4 className="font-medium text-gray-900 mb-2" data-id="element-2769">Deskripsi</h4>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2770">
                {selectedTask.task.description || 'Tidak ada deskripsi detail.'}
              </p>
            </div>

            {selectedTask.task.requirements && <div data-id="element-2771">
                <h4 className="font-medium text-gray-900 mb-2" data-id="element-2772">
                  Persyaratan Bukti
                </h4>
                <ul className="space-y-2" data-id="element-2773">
                  {selectedTask.task.requirements.map((req, i) => <li key={i} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-2774">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary-500" data-id="element-2775"></div>
                      {req}
                    </li>)}
                </ul>
              </div>}

            <div data-id="element-2776">
              <h4 className="font-medium text-gray-900 mb-2" data-id="element-2777">Upload Bukti</h4>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-2778">
                <Camera className="h-8 w-8 text-gray-400 mb-2" data-id="element-2779" />
                <p className="text-xs text-gray-500" data-id="element-2780">Tap untuk ambil foto</p>
              </div>
            </div>

            {selectedTask.task.materialLink && <Button variant="outline" className="w-full justify-between" data-id="element-2781">
                <span data-id="element-2782">Lihat Materi Pendukung</span>
                <ExternalLink className="h-4 w-4" data-id="element-2783" />
              </Button>}

            <div data-id="element-2784">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-2785">
                Catatan Lapangan
              </label>
              <Textarea placeholder="Tambahkan catatan kendala atau progress..." className="text-sm" data-id="element-2786" />
            </div>
          </div>}
      </Modal>
    </OfficerLayout>;
}